<script setup>
import { isVNode, computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import Policy from 'dashboard/components/policy.vue';
import { useSidebarContext } from './provider';

const props = defineProps({
  name: { type: String, default: '' },
  label: { type: String, required: true },
  to: { type: [String, Object], required: true },
  icon: { type: [String, Object], default: null },
  active: { type: Boolean, default: false },
  component: { type: Function, default: null },
  count: { type: Number, default: 0 },
});

const { resolvePermissions, resolveFeatureFlag } = useSidebarContext();

const shouldRenderComponent = computed(() => {
  return typeof props.component === 'function' || isVNode(props.component);
});

const displayCount = computed(() => {
  const n = Number(props.count || 0);
  if (!n) return '';
  return n > 99 ? '99+' : String(n);
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Policy
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="py-0.5 ltr:pl-3 rtl:pr-3 rtl:mr-3 ltr:ml-3 relative child-item before:bg-n-slate-4 after:bg-transparent after:border-n-slate-4 before:left-0 rtl:before:right-0 wlab-sidebar-leaf"
  >
    <component
      :is="to ? 'router-link' : 'div'"
      :to="to"
      :title="label"
      class="flex h-8 items-center gap-2 px-2 py-1 rounded-lg max-w-[9.438rem] hover:bg-gradient-to-r from-transparent via-n-slate-3/70 to-n-slate-3/70 group wlab-sidebar-leaf-link"
      :class="{
        'bg-n-alpha-2 active': active,
      }"
    >
      <component
        :is="component"
        v-if="shouldRenderComponent"
        :label
        :icon
        :active
        :count="count"
      />
      <template v-else>
        <Icon v-if="icon" :icon="icon" class="size-4 inline-block" />
        <div class="flex-1 truncate min-w-0">{{ label }}</div>
        <span
          v-if="displayCount"
          class="rounded-md text-xs leading-5 font-medium text-center outline outline-1 px-1 flex-shrink-0 text-n-slate-11 outline-n-strong"
        >
          {{ displayCount }}
        </span>
      </template>
    </component>
  </Policy>
</template>

<style>
.wlab-sidebar-leaf,
.wlab-sidebar-leaf *,
.wlab-sidebar-leaf-link,
.wlab-sidebar-leaf-link *,
.wlab-sidebar-leaf-link div,
.wlab-sidebar-leaf-link span,
.wlab-sidebar-leaf-link .text-xs,
.wlab-sidebar-leaf-link .text-sm {
  font-family: 'Excon', sans-serif !important;
  font-size: 12px !important;
}

/* Conversation-only: compact leaf styling (light + dark) */
.wlab-sidebar--conversation .wlab-sidebar-leaf-link,
.wlab-sidebar--conversation .wlab-sidebar-leaf-link * {
  font-size: 9px !important;
}

.wlab-sidebar--conversation .wlab-sidebar-leaf-link {
  min-height: 25px !important;
  height: auto !important;
  padding: 2px 12px !important;
  border-radius: 5px !important;
  max-width: none !important;
}

.wlab-sidebar--conversation .wlab-sidebar-leaf-link span.rounded-md.text-xs {
  font-size: 9px !important;
  line-height: 1.1 !important;
}

/* Light mode - Black text */
body:not(.dark) .wlab-sidebar-leaf,
body:not(.dark) .wlab-sidebar-leaf * {
  color: #000000 !important;
}

/* Dark mode - White text */
body.dark .wlab-sidebar-leaf,
body.dark .wlab-sidebar-leaf * {
  color: #ffffff !important;
}

/* ===== HOVER ONLY - Orange ===== */

/* Light mode - Orange ONLY on hover (not when active) */
body:not(.dark) .wlab-sidebar-leaf-link:hover:not(.active):not(.bg-n-alpha-2) {
  background: linear-gradient(
    to right,
    transparent,
    rgba(0, 102, 255, 0.08),
    rgba(0, 102, 255, 0.15)
  ) !important;
}

body:not(.dark) .wlab-sidebar-leaf-link:hover * {
  color: #004db3 !important;
}

/* Light mode - Active item (selected) - stronger orange */
body:not(.dark) .wlab-sidebar-leaf-link.active {
  background: rgba(0, 102, 255, 0.18) !important;
}

body:not(.dark) .wlab-sidebar-leaf-link.active * {
  color: #004db3 !important;
}

/* Dark mode - Orange ONLY on hover (not when active) */
body.dark .wlab-sidebar-leaf-link:hover:not(.active):not(.bg-n-alpha-2) {
  background: linear-gradient(
    to right,
    transparent,
    rgba(51, 136, 255, 0.1),
    rgba(51, 136, 255, 0.18)
  ) !important;
}

body.dark .wlab-sidebar-leaf-link:hover * {
  color: #66a3ff !important;
}

/* Dark mode - Active item (selected) - stronger orange */
body.dark .wlab-sidebar-leaf-link.active {
  background: rgba(51, 136, 255, 0.22) !important;
}

body.dark .wlab-sidebar-leaf-link.active * {
  color: #66a3ff !important;
}
</style>
